import { useState, useEffect } from "react";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { kvService } from "@/lib/kv-service";
import { realtimeService, RealtimeEvent } from "@/lib/realtime-service";
import { DEFAULT_JOBS } from "@/data/test-data";
import { generateUUID } from "@/lib/uuid";

// Always use Cloudflare KV for data storage
const dataService = kvService;

export function useDataService() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [jobs, setJobs] = useState<string[]>(DEFAULT_JOBS);
  const [isLoading, setIsLoading] = useState(true);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const [isRealtimeEnabled, setIsRealtimeEnabled] = useState(false);

  useEffect(() => {
    loadData();
    
    // Subscribe to real-time events
    const unsubscribe = realtimeService.subscribe(handleRealtimeEvent);
    
    // Check real-time connection status
    const checkConnection = () => {
      setIsRealtimeConnected(realtimeService.isConnectedToRealtime());
      setIsRealtimeEnabled(realtimeService.isRealtimeEnabled());
    };
    
    checkConnection();
    const connectionInterval = setInterval(checkConnection, 5000);
    
    return () => {
      unsubscribe();
      clearInterval(connectionInterval);
    };
  }, []);

  const handleRealtimeEvent = (event: RealtimeEvent) => {
    console.log('Real-time event received:', event.type, event);
    
    switch (event.type) {
      case 'customer_added':
        setCustomers(prev => {
          // Avoid duplicates by ID or customer code
          if (prev.some(c => c.id === event.data.id || c.customerCode === event.data.customerCode)) {
            console.log("Duplicate customer detected, skipping real-time update");
            return prev;
          }
          console.log("Adding customer from real-time:", event.data.fullName);
          return [event.data, ...prev];
        });
        break;
        
      case 'customer_updated':
        setCustomers(prev => 
          prev.map(c => c.id === event.data.id ? event.data : c)
        );
        break;
        
      case 'customer_deleted':
        setCustomers(prev => prev.filter(c => c.id !== event.data.id));
        break;
        
      case 'product_added':
        setProducts(prev => {
          // Avoid duplicates
          if (prev.some(p => p.id === event.data.id)) return prev;
          return [event.data, ...prev];
        });
        break;
        
      case 'product_updated':
        setProducts(prev => 
          prev.map(p => p.id === event.data.id ? event.data : p)
        );
        break;
        
      case 'product_deleted':
        setProducts(prev => prev.filter(p => p.id !== event.data.id));
        break;
        
      case 'jobs_updated':
        setJobs(event.data);
        break;
        
      case 'payment_made':
        // Payment events are handled by product_updated events
        console.log(`Payment of ${event.data.amount} made for product ${event.data.productId}`);
        break;
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [customersData, productsData, jobsData] = await Promise.all([
        dataService.getCustomers(),
        dataService.getProducts(),
        dataService.getJobs(),
      ]);

      setCustomers(customersData);
      setProducts(productsData);
      setJobs(jobsData.length > 0 ? jobsData : DEFAULT_JOBS);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Customer methods
  const addCustomer = async (customerData: Omit<Customer, "id" | "createdAt">) => {
    const newCustomer: Customer = {
      ...customerData,
      id: generateUUID(),
      createdAt: new Date(),
    };

    try {
      // Optimistic update - add immediately for UI feedback
      setCustomers((prev) => {
        // Check if customer with same code already exists to prevent duplicates
        if (prev.some(c => c.customerCode === newCustomer.customerCode)) {
          console.warn("Customer with this code already exists, skipping optimistic update");
          return prev;
        }
        return [newCustomer, ...prev];
      });
      
      await dataService.createCustomer(newCustomer);
      return newCustomer;
    } catch (error) {
      console.error("Failed to add customer:", error);
      // Revert optimistic update on error
      setCustomers((prev) => prev.filter(c => c.id !== newCustomer.id));
      throw error;
    }
  };

  const updateCustomer = async (updatedCustomer: Customer) => {
    // Store previous state for rollback
    const previousCustomers = customers;
    
    try {
      // Optimistic update
      setCustomers((prev) =>
        prev.map((c) => (c.id === updatedCustomer.id ? updatedCustomer : c))
      );
      
      await dataService.updateCustomer(updatedCustomer);
      // Real-time update will be handled by the event listener
    } catch (error) {
      console.error("Failed to update customer:", error);
      // Rollback on error
      setCustomers(previousCustomers);
      throw error;
    }
  };

  const deleteCustomer = async (id: string) => {
    // Store previous state for rollback
    const previousCustomers = customers;
    
    try {
      // Optimistic update
      setCustomers((prev) => prev.filter((c) => c.id !== id));
      
      await dataService.deleteCustomer(id);
      // Real-time update will be handled by the event listener
    } catch (error) {
      console.error("Failed to delete customer:", error);
      // Rollback on error
      setCustomers(previousCustomers);
      throw error;
    }
  };

  // Product methods
  const addProduct = async (productData: Omit<Product, "id" | "createdAt" | "remainingAmount">) => {
    const newProduct: Product = {
      ...productData,
      id: generateUUID(),
      deviceDescription: productData.deviceDescription || "",
      installments: productData.installments || 0,
      remainingAmount: productData.salePrice - productData.paidAmount,
      createdAt: new Date(),
    };

    try {
      // Optimistic update FIRST - add immediately for UI feedback
      setProducts((prev) => {
        // Check if product with same ID already exists to prevent duplicates
        if (prev.some(p => p.id === newProduct.id)) {
          console.warn("Product with this ID already exists, skipping optimistic update");
          return prev;
        }
        return [newProduct, ...prev];
      });
      
      await dataService.createProduct(newProduct);
      // Real-time event will be ignored because we already have this product (duplicate check in handleRealtimeEvent)
      return newProduct;
    } catch (error) {
      console.error("Failed to add product:", error);
      // Revert optimistic update on error
      setProducts((prev) => prev.filter(p => p.id !== newProduct.id));
      throw error;
    }
  };

  const updateProduct = async (updatedProduct: Product) => {
    // Store previous state for rollback
    const previousProducts = products;
    
    try {
      // Optimistic update
      setProducts((prev) =>
        prev.map((p) => (p.id === updatedProduct.id ? updatedProduct : p))
      );
      
      await dataService.updateProduct(updatedProduct);
      // Real-time update will be handled by the event listener
    } catch (error) {
      console.error("Failed to update product:", error);
      // Rollback on error
      setProducts(previousProducts);
      throw error;
    }
  };

  const deleteProduct = async (id: string) => {
    // Store previous state for rollback
    const previousProducts = products;
    
    try {
      // Optimistic update
      setProducts((prev) => prev.filter((p) => p.id !== id));
      
      await dataService.deleteProduct(id);
      // Real-time update will be handled by the event listener
    } catch (error) {
      console.error("Failed to delete product:", error);
      // Rollback on error
      setProducts(previousProducts);
      throw error;
    }
  };

  const updatePayment = async (id: string, paidAmount: number) => {
    const product = products.find((p) => p.id === id);
    if (!product) return;

    const updatedProduct = {
      ...product,
      paidAmount,
      remainingAmount: product.salePrice - paidAmount,
    };

    await updateProduct(updatedProduct);
  };

  const handlePayment = async (productId: string, amount: number) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    const updatedProduct = {
      ...product,
      paidAmount: product.paidAmount + amount,
      remainingAmount: product.salePrice - (product.paidAmount + amount),
      installments: (product.installments || 0) + 1,
    };

    await updateProduct(updatedProduct);
  };

  // Jobs methods
  const addJob = async (job: string) => {
    const updatedJobs = [...jobs, job];
    
    try {
      // Optimistic update
      setJobs(updatedJobs);
      
      await dataService.updateJobs(updatedJobs);
      // Real-time update will be handled by the event listener
    } catch (error) {
      console.error("Failed to add job:", error);
      // Rollback on error
      setJobs(jobs);
      throw error;
    }
  };

  const deleteJob = async (job: string) => {
    const updatedJobs = jobs.filter((j) => j !== job);
    const previousJobs = jobs;
    
    try {
      // Optimistic update
      setJobs(updatedJobs);
      
      await dataService.updateJobs(updatedJobs);
      // Real-time update will be handled by the event listener
    } catch (error) {
      console.error("Failed to delete job:", error);
      // Rollback on error
      setJobs(previousJobs);
      throw error;
    }
  };

  return {
    // Data
    customers,
    products,
    jobs,
    isLoading,
    isRealtimeConnected,
    isRealtimeEnabled,
    
    // Methods
    addCustomer,
    updateCustomer,
    deleteCustomer,
    addProduct,
    updateProduct,
    deleteProduct,
    updatePayment,
    handlePayment,
    addJob,
    deleteJob,
    
    // Utility
    refreshData: loadData,
  };
}